import React from "react";
import Icon from "../icons";
import { ElementType } from "@/constants/tagular/main";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import useElementEvents from "@/hooks/eventing/useElementEvents";

interface TableRow {
  rowHeader?: {
    text: string;
    rowSpan?: number;
  };
  cells: React.ReactNode[];
}

interface TableData {
  headers: string[];
  rows: TableRow[];
}

function extractTextFromElement(element: React.ReactNode): string {
  if (typeof element === "string") {
    return element;
  }
  if (React.isValidElement(element)) {
    const children = (element as React.ReactElement<any>).props.children;
    if (typeof children === "string") {
      return children;
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromElement).join("");
    }
    return extractTextFromElement(children);
  }
  return "";
}

function extractTableData(children: React.ReactNode): TableData | null {
  if (!React.isValidElement(children)) {
    return null;
  }

  // Find the table element
  const tableElement = React.Children.toArray(
    (children as React.ReactElement<any>).props.children,
  ).find((child: any) => React.isValidElement(child) && child.type === "table");

  if (!tableElement || !React.isValidElement(tableElement)) {
    return null;
  }

  const tableChildren = React.Children.toArray(
    (tableElement as React.ReactElement<any>).props.children,
  );

  // Extract headers
  const theadElement = tableChildren.find(
    (child: any) => React.isValidElement(child) && child.type === "thead",
  );
  let headers: string[] = [];

  if (theadElement && React.isValidElement(theadElement)) {
    const theadChildren = React.Children.toArray(
      (theadElement as React.ReactElement<any>).props.children,
    );
    const headerRow = theadChildren.find(
      (child: any) => React.isValidElement(child) && child.type === "tr",
    );

    if (headerRow && React.isValidElement(headerRow)) {
      const headerCells = React.Children.toArray(
        (headerRow as React.ReactElement<any>).props.children,
      );
      headers = headerCells
        .filter((cell: any) => React.isValidElement(cell) && cell.type === "th")
        .map((cell: any) => {
          const cellElement = cell as React.ReactElement<any>;
          return extractTextFromElement(cellElement.props.children);
        });
    }
  }

  // Extract rows
  const tbodyElement = tableChildren.find(
    (child: any) => React.isValidElement(child) && child.type === "tbody",
  );
  let rows: TableRow[] = [];

  if (tbodyElement && React.isValidElement(tbodyElement)) {
    const tbodyChildren = React.Children.toArray(
      (tbodyElement as React.ReactElement<any>).props.children,
    );

    rows = tbodyChildren
      .filter(
        (child: any) => React.isValidElement(child) && child.type === "tr",
      )
      .map((row: any) => {
        const rowElement = row as React.ReactElement<any>;
        const rowCells = React.Children.toArray(rowElement.props.children);

        // Find row header (th with scope="row")
        const rowHeaderCell = rowCells.find(
          (cell: any) =>
            React.isValidElement(cell) &&
            cell.type === "th" &&
            (cell as React.ReactElement<any>).props.scope === "row",
        );

        let rowHeader: TableRow["rowHeader"] = undefined;
        if (rowHeaderCell && React.isValidElement(rowHeaderCell)) {
          const headerElement = rowHeaderCell as React.ReactElement<any>;
          rowHeader = {
            text: extractTextFromElement(headerElement.props.children),
            rowSpan: headerElement.props.rowSpan,
          };
        }

        // Extract data cells (keep original React nodes for links, etc.)
        const dataCells = rowCells.filter(
          (cell: any) => React.isValidElement(cell) && cell.type === "td",
        );

        return {
          rowHeader,
          cells: dataCells.map(
            (cell: any) => (cell as React.ReactElement<any>).props.children,
          ),
        };
      });
  }

  return { headers, rows };
}

function renderCellContent(
  content: React.ReactNode,
  groupIdx: number,
  cellIdx: number,
  rowIdx: number,
  handleLinkClick?: (
    href: string,
    text: string,
    groupIdx: number,
    cellIdx: number,
    rowIdx: number,
  ) => void,
): React.ReactNode {
  const text = extractTextFromElement(content);
  const divClass = "my-[2px]";

  // Convert yes/no to icons
  if (text.toLowerCase() === "yes") {
    return (
      <div className={divClass}>
        <Icon
          type="circle-check-fill"
          svgProps={{ width: "24", height: "24" }}
          pathProps={{ fill: "#1F453D" }}
        />
      </div>
    );
  }
  if (text.toLowerCase() === "no") {
    return (
      <div className={divClass}>
        <Icon
          type="circle-close-fill"
          svgProps={{ width: "24", height: "24" }}
          pathProps={{ fill: "#D74000" }}
        />
      </div>
    );
  }

  // Handle link elements - only track if handleLinkClick is provided
  if (
    React.isValidElement(content) &&
    content.type === "a" &&
    handleLinkClick
  ) {
    const linkProps = (content as React.ReactElement<any>).props;
    const href = linkProps.href || "";
    const linkText = extractTextFromElement(linkProps.children);

    return React.cloneElement(content as React.ReactElement<any>, {
      onClick: (e: React.MouseEvent) => {
        handleLinkClick(href, linkText, groupIdx, cellIdx, rowIdx);
        // Allow default behavior (navigation)
      },
    });
  }

  // Return original content (preserves links, etc.)
  return content;
}

function assignRowColors(rows: TableRow[]) {
  let groupIndex = 0;

  // First pass: assign colors and track groups
  const rowsWithMetadata = rows.map((row, rowIdx) => {
    let bgClass: string;
    let groupId: number;

    if (row.rowHeader) {
      // This row starts a new group
      bgClass = groupIndex % 2 === 0 ? "bg-white" : "bg-putty-400";
      groupId = groupIndex;
      groupIndex++;
    } else {
      // This row continues the previous group's color
      bgClass = (groupIndex - 1) % 2 === 0 ? "bg-white" : "bg-putty-400";
      groupId = groupIndex - 1;
    }

    return { ...row, bgClass, rowIdx, groupId };
  });

  // Second pass: restructure into groups with merged cells
  const groups: Array<{
    rowHeader?: { text: string; rowSpan?: number };
    bgClass: string;
    cellsByColumn: React.ReactNode[][];
  }> = [];

  let currentGroup: (typeof groups)[0] | null = null;

  rowsWithMetadata.forEach((row) => {
    if (row.rowHeader) {
      // Start a new group
      if (currentGroup) {
        groups.push(currentGroup);
      }
      currentGroup = {
        rowHeader: row.rowHeader,
        bgClass: row.bgClass,
        cellsByColumn: row.cells.map((cell) => [cell]),
      };
    } else if (currentGroup) {
      // Add cells to current group
      row.cells.forEach((cell, idx) => {
        if (currentGroup) {
          if (!currentGroup.cellsByColumn[idx]) {
            currentGroup.cellsByColumn[idx] = [];
          }
          currentGroup.cellsByColumn[idx].push(cell);
        }
      });
    }
  });

  // Push the last group
  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}

export const DesktopTable = ({ children }: { children: React.ReactNode }) => {
  const tableData = extractTableData(children);

  const baseWebElement: ElementClicked["webElement"] = {
    elementType: ElementType.Link,
    location: "responsive-table-desktop",
  };

  const { elementClicked: triggerElementClick } = useElementEvents({
    webElement: baseWebElement,
  });

  const handleLinkClick = (
    href: string,
    text: string,
    groupIdx: number,
    cellIdx: number,
    rowIdx: number,
  ) => {
    const itemWebElement: ElementClicked["webElement"] = {
      elementType: ElementType.Link,
      htmlId: `table-link-group-${groupIdx}-cell-${cellIdx}-row-${rowIdx}`,
      location: "responsive-table-desktop",
      position: `group-${groupIdx}-cell-${cellIdx}-row-${rowIdx}`,
      text: text,
    };

    triggerElementClick({
      webElement: itemWebElement,
      outboundUrl: href,
    });
  };

  if (!tableData) {
    return null;
  }

  const rowGroups = assignRowColors(tableData.rows);
  const headerCount = tableData.headers.length;

  return (
    <div className="responsive-table hidden md:block overflow-hidden rounded-2xl shadow-md my-4">
      <table className="responsive-table__table w-full text-base my-0">
        <thead className="responsive-table__header">
          <tr>
            {tableData.headers.map((header, idx) => (
              <th
                key={idx}
                className={`text-white px-6 py-4 text-left font-semibold ${
                  idx === 0
                    ? "rounded-tl-2xl"
                    : idx === headerCount - 1
                      ? "rounded-tr-2xl"
                      : ""
                }`}
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rowGroups.map((group, groupIdx) => {
            const isLastGroup = groupIdx === rowGroups.length - 1;

            return (
              <tr
                key={groupIdx}
                className={`${group.bgClass} border-b border-gray-300`}
              >
                {group.rowHeader && (
                  <th
                    scope="row"
                    className={`px-6 py-4 text-left font-bold ${
                      isLastGroup ? "rounded-bl-2xl" : ""
                    }`}
                  >
                    {group.rowHeader.text}
                  </th>
                )}

                {group.cellsByColumn.map((columnCells, cellIdx) => {
                  const isLastCell = cellIdx === group.cellsByColumn.length - 1;

                  return (
                    <td
                      key={cellIdx}
                      className={`px-6 py-4 align-top ${
                        isLastGroup && isLastCell && !group.rowHeader
                          ? "rounded-br-2xl"
                          : ""
                      }`}
                    >
                      <div className="flex flex-col gap-4">
                        {columnCells.map((cell, idx) => (
                          <div key={idx}>
                            {renderCellContent(
                              cell,
                              groupIdx,
                              cellIdx,
                              idx,
                              handleLinkClick,
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
